import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Contact.css';

interface ContactProps {
  personalInfo: {
    email: string;
    lookingFor: string;
  };
  social: {
    linkedin: string;
    github: string;
    twitter: string;
    whatsapp?: string;
  };
}

type SubmitStatus = 'idle' | 'sending' | 'success' | 'error';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[0-9\s\-()]{7,20}$/;

const Contact: React.FC<ContactProps> = ({ personalInfo, social }) => {
  const { t } = useTranslation('portfolio');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const validateEmail = (value: string) => EMAIL_REGEX.test(value.trim());
  const validatePhone = (value: string) => PHONE_REGEX.test(value.trim());

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'sending') return;

    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();

    let hasError = false;
    if (!validateEmail(trimmedEmail)) {
      setEmailError(t('contactForm.invalidEmail'));
      hasError = true;
    } else {
      setEmailError('');
    }

    if (!validatePhone(trimmedPhone)) {
      setPhoneError(t('contactForm.invalidPhone'));
      hasError = true;
    } else {
      setPhoneError('');
    }

    if (hasError) return;

    setStatus('sending');

    try {
      const response = await fetch(
        `https://formsubmit.co/ajax/${personalInfo.email}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            name,
            email: trimmedEmail,
            phone: trimmedPhone,
            subject: subject || `New message from ${name}`,
            message,
            _template: 'table',
            _captcha: 'false',
          }),
        }
      );

      if (!response.ok) throw new Error('Request failed');

      setStatus('success');
      setName('');
      setEmail('');
      setPhone('');
      setSubject('');
      setMessage('');
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="contact-section" id="contact">
      <div className="contact-section-inner">
        <div className="contact-header">
          <div className="contact-title-group">
            <div className="contact-title-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="2" y="3.5" width="12" height="9" rx="1.5" stroke="white" strokeWidth="1.33" />
                <path d="M3 5L8 9L13 5" stroke="white" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 className="contact-title">تواصل معي</h2>
          </div>
          <p className="contact-subtitle">{personalInfo.lookingFor}</p>
          <a href={`mailto:${personalInfo.email}`} className="contact-email-pill">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <rect x="1.75" y="3" width="10.5" height="8" rx="1.17" stroke="#0369A1" strokeWidth="1.17" />
              <path d="M2 4L7 7.5L12 4" stroke="#0369A1" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>{personalInfo.email}</span>
          </a>
        </div>

        <form className="contact-form-card" onSubmit={handleSubmit} noValidate>
          <h3 className="contact-form-heading">{t('contactForm.heading')}</h3>

          <div className="contact-form-row">
            <div className="contact-field">
              <label className="contact-label" htmlFor="cf-name">الاسم</label>
              <input
                id="cf-name"
                type="text"
                name="name"
                className="contact-input"
                placeholder={t('contactForm.namePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={status === 'sending'}
              />
            </div>
            <div className="contact-field">
              <label className="contact-label" htmlFor="cf-email">البريد الإلكتروني</label>
              <input
                id="cf-email"
                type="email"
                name="email"
                className={`contact-input${emailError ? ' is-invalid' : ''}`}
                placeholder={t('contactForm.emailPlaceholder')}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError('');
                }}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v && !validateEmail(v)) {
                    setEmailError(t('contactForm.invalidEmail'));
                  }
                }}
                required
                disabled={status === 'sending'}
                aria-invalid={!!emailError}
              />
              {emailError && <span className="contact-error">{emailError}</span>}
            </div>
          </div>

          <div className="contact-form-row">
            <div className="contact-field">
              <label className="contact-label" htmlFor="cf-phone">رقم الجوال</label>
              <input
                id="cf-phone"
                type="tel"
                name="phone"
                className={`contact-input${phoneError ? ' is-invalid' : ''}`}
                placeholder={t('contactForm.phonePlaceholder')}
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  if (phoneError) setPhoneError('');
                }}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v && !validatePhone(v)) {
                    setPhoneError(t('contactForm.invalidPhone'));
                  }
                }}
                required
                disabled={status === 'sending'}
                aria-invalid={!!phoneError}
              />
              {phoneError && <span className="contact-error">{phoneError}</span>}
            </div>
            <div className="contact-field">
              <label className="contact-label" htmlFor="cf-subject">الموضوع</label>
              <input
                id="cf-subject"
                type="text"
                name="subject"
                className="contact-input"
                placeholder={t('contactForm.subjectPlaceholder')}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={status === 'sending'}
              />
            </div>
          </div>

          <div className="contact-field">
            <label className="contact-label" htmlFor="cf-message">الرسالة</label>
            <textarea
              id="cf-message"
              name="message"
              className="contact-input contact-textarea"
              placeholder={t('contactForm.messagePlaceholder')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              rows={5}
              disabled={status === 'sending'}
            />
          </div>

          <div className="contact-form-footer">
            <div className="contact-socials">
              {social.twitter && (
                <a href={social.twitter} target="_blank" rel="noopener noreferrer" className="contact-social" aria-label="Twitter / X">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
              )}
              {social.linkedin && (
                <a href={social.linkedin} target="_blank" rel="noopener noreferrer" className="contact-social" aria-label="LinkedIn">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 11.001-4.125 2.062 2.062 0 010 4.125zM7.119 20.452H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451c.98 0 1.778-.773 1.778-1.729V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
              )}
              {social.github && (
                <a href={social.github} target="_blank" rel="noopener noreferrer" className="contact-social" aria-label="GitHub">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                  </svg>
                </a>
              )}
              {social.whatsapp && (
                <a href={social.whatsapp} target="_blank" rel="noopener noreferrer" className="contact-social" aria-label="WhatsApp">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                  </svg>
                </a>
              )}
            </div>

            <button type="submit" className="contact-submit" disabled={status === 'sending'}>
              <span>{status === 'sending' ? t('contactForm.sending') : t('contactForm.submit')}</span>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M9 3.5L5.5 7L9 10.5" stroke="white" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          {status === 'success' && (
            <p className="contact-status contact-status-success">{t('contactForm.success')}</p>
          )}
          {status === 'error' && (
            <p className="contact-status contact-status-error">{t('contactForm.error')}</p>
          )}
        </form>
      </div>
    </section>
  );
};

export default Contact;
